/**
 * Disclosure Nav
 *
 * (c)2023 - Joe Bondra
 */

"use strict";
import hoverintent from "./hoverintent.min.js";
export default class disclosureNav {
  constructor(selector, options = {}) {
    // Define members.
    this._keyCode = {
      TAB: 9,
      ENTER: 13,
      ESC: 27,
      SPACE: 32,
      END: 35,
      HOME: 36,
      ARROW_LEFT: 37,
      ARROW_UP: 38,
      ARROW_RIGHT: 39,
      ARROW_DOWN: 40,
    };

    // Merge user-defined options with default options.
    this._defaultOptions = {
      windowObj: window,
      domObj: document,
      hoverintent: hoverintent,
      ariaLabel: "",
      orientation: "horizontal",
      useHover: true,
      useResponsiveMenu: true,
      breakpointMinWidth: 500,
      responsiveMenuButtonText: "Menu",
      collapseVertical: false,
      activeClass: "",
      expandToActive: false,
      expandActiveSubmenu: false,
      buttonAriaLabelPrefix: "More ",
      buttonAriaLabelSuffix: " pages",
    };
    this._options = Object.assign(this._defaultOptions, options);

    // Flag for determining if orientation has been dynamically switched.
    this._orientationSwitched = false;

    this._selector = selector;
    this._navElem = this._options.domObj.querySelector(this._selector);

    if (this._options.ariaLabel.length > 0) {
      this._navElem.setAttribute("aria-label", this._options.ariaLabel);
    }
    this._navElem.classList.add("disclosure-nav");

    let orientation = this._options.orientation;
    if (orientation === "horizontal") {
      this._navElem.classList.add("disclosure-nav-orientation-horizontal");
    } else if (orientation === "vertical") {
      this._navElem.classList.add("disclosure-nav-orientation-vertical");
    }

    // Add keyboard events for top level anchors <A>.
    let topLevelAElems = this._options.domObj.querySelectorAll(
      "nav.disclosure-nav > ul > li > a, nav.disclosure-nav > ol > li > a",
    );
    for (let i = 0; i < topLevelAElems.length; i++) {
      topLevelAElems[i].addEventListener("keydown", this.handleKeyDownTopLevelAElem.bind(this));
    }

    let submenuElems = this._navElem.querySelectorAll("li > ul, li > ol");
    let randomId = Math.random().toString().replace(".", "_");

    for (let i = 0; i < submenuElems.length; i++) {
      submenuElems[i].classList.add("disclosure-nav-submenu");
      submenuElems[i].classList.add("disclosure-nav-submenu-closed");

      let submenuElemId = `disclosure-nav-sublist-${i}-${randomId}`;
      submenuElems[i].setAttribute("id", submenuElemId);

      let isTopLevel =
        submenuElems[i].parentNode.parentNode.parentNode.nodeName === "NAV" ? true : false;

      let button = this._options.domObj.createElement("button");
      button.setAttribute("type", "button");
      button.setAttribute("aria-expanded", "false");
      button.setAttribute("aria-controls", submenuElemId);
      button.classList.add("disclosure-nav-button");
      if (isTopLevel) {
        button.classList.add("disclosure-nav-top-level");
      }
      button.addEventListener("click", this.handleButtonClick.bind(this));
      submenuElems[i].parentNode.insertBefore(button, submenuElems[i]);

      // Check for a 'top-level' link.
      let navItem = submenuElems[i].parentNode.querySelector("a, span");
      if (navItem != null) {
        let navText = navItem.innerHTML;
        if (navItem.nodeName === "A") {
          if (navItem.hasAttribute("data-button-aria-label")) {
            let buttonAriaLabel = navItem.dataset.buttonAriaLabel;
            button.setAttribute("aria-label", buttonAriaLabel);
          } else {
            let buttonAriaLabel =
              this._options.buttonAriaLabelPrefix + navText + this._options.buttonAriaLabelSuffix;
            button.setAttribute("aria-label", buttonAriaLabel);
          }
        } else if (navItem.nodeName === "SPAN") {
          // Turn span into button.
          button.innerHTML = navText;
          button.classList.add("disclosure-nav-button-no-link");
          // Move 'active' class to button (if applicable).
          if (navItem.classList.contains(this._options.activeClass)) {
            button.classList.add(this._options.activeClass);
          }
          navItem.remove();
        }
        // Mark links as having a submenu.
        navItem.classList.add("disclosure-nav-item-with-submenu");
      }

      // Add keyboard handlers to buttons and submenu items.
      button.addEventListener("keydown", this.handleKeyDownButton.bind(this));

      let submenuItems = submenuElems[i].children;
      for (let j = 0; j < submenuItems.length; j++) {
        let submenuItem = submenuItems[j].querySelector("li > a, li > span");
        submenuItem.addEventListener("keydown", this.handleKeyDownSubmenuItem.bind(this));
      }
    }

    // Close all submenus when nav element loses focus.
    this._navElem.addEventListener("focusout", this.handleFocusoutNavElem.bind(this));

    // Add hover events if required.
    if (this._options.useHover) {
      // Close all submenus when mouse hover is no longer in submenu.
      if (this._options.hoverintent) {
        // Hoverintent is in environment.
        let options = {
          timeout: 900,
          interval: 100,
        };

        this._options
          .hoverintent(this._navElem, function (event) {}, this.handleMouseoutNavElem.bind(this))
          .options(options);
      } else {
        // Default mouse events.
        this._navElem.addEventListener("mouseout", this.handleMouseoutNavElem.bind(this));
      }

      let navItemElems = this._navElem.querySelectorAll("li");

      for (let i = 0; i < navItemElems.length; i++) {
        // Add hoverintent functionality (or mouse events if hoverintent not available).
        if (this._options.hoverintent) {
          // Hoverintent in environment.
          let options = {
            timeout: 0,
            interval: 100,
          };

          this._options
            .hoverintent(
              navItemElems[i],
              this.handleMouseoverNavItem.bind(this),
              function (event) {},
            )
            .options(options);
        } else {
          // Default mouse events.
          navItemElems[i].addEventListener("mouseover", this.handleMouseoverNavItem.bind(this));
        }
      }
    }

    if (this._options.useResponsiveMenu) {
      // Create menu toggle.
      let navElemId = this._navElem.getAttribute("id");
      this._menuToggle = this._options.domObj.createElement("button");
      this._menuToggle.textContent = this._options.responsiveMenuButtonText;
      this._menuToggle.setAttribute("id", "disclosure-nav-" + navElemId + "-toggle");
      this._menuToggle.setAttribute("aria-expanded", "false");
      this._menuToggle.setAttribute("aria-controls", navElemId);
      this._menuToggle.classList.add("disclosure-nav-toggle");
      let menuIcon = this._options.domObj.createElement("span");
      menuIcon.setAttribute("aria-hidden", "true");
      this._menuToggle.appendChild(menuIcon);
      this._menuToggle.addEventListener("click", this.handleClickMenuToggle.bind(this));

      // Add resize handler.
      this._options.windowObj.addEventListener("resize", this.handleWindowResize.bind(this));

      // Resize on construction in case breakpoint is already met.
      this.resize();
    }

    if (this._options.expandToActive) {
      // Expand to element marked with activeClass if desired.
      this.expandToActiveMenuitem();
    }

    if (this._options.expandActiveSubmenu) {
      // Expand submenu of element marked with activeClass.
      this.expandActiveSubmenu();
    }
  }

  // Event handlers -----------------------------------------------------------

  handleFocusoutNavElem(event) {
    if (event.defaultPrevented) {
      return;
    }

    let newTarget = event.relatedTarget;

    if (newTarget == null || !this._navElem.contains(newTarget)) {
      if (
        !this.isResponsiveMenu() &&
        !(this._options.orientation == "vertical" && this._options.collapseVertical == false)
      ) {
        this.closeAllSubmenus();
      }
    }
  }

  handleButtonClick(event) {
    if (event.defaultPrevented) {
      return;
    }

    let button = event.target;
    let submenu = this.getSubmenu(button);

    if (
      !this.isResponsiveMenu() &&
      !(this._options.orientation == "vertical" && this._options.collapseVertical == false)
    ) {
      this.closeSiblingSubmenus(submenu);
    }
    this.toggleSubmenu(submenu);
  }

  handleKeyDownButton(event) {
    if (event.defaultPrevented) {
      return;
    }

    let key = this.normalizeKey(event.key || event.keyCode);
    let button = event.target;
    let submenu = this.getSubmenu(button);

    if (key == this._keyCode.ESC) {
      if (submenu.classList.contains("disclosure-nav-submenu-open")) {
        this.toggleSubmenu(submenu);
      } else if (submenu.classList.contains("disclosure-nav-submenu-closed")) {
        if (!this.isTopLevelButton(button)) {
          // Close submenu containing button and move focus to button which controls the submenu.
          let liElem = button.parentNode.parentNode.parentNode;
          submenu = this.getSubmenu(liElem);
          button = this.getButton(submenu);
          this.toggleSubmenu(submenu);
          button.focus();
        } else if (this.isTopLevelButton(button) && this.isResponsiveMenu()) {
          let menuButton = this._options.domObj.querySelector("#disclosure-nav-main-nav-toggle");
          this.toggleResponsiveMenu();
          menuButton.focus();
        }
      }
    }
  }

  handleKeyDownTopLevelAElem(event) {
    if (event.defaultPrevented) {
      return;
    }

    let key = this.normalizeKey(event.key || event.keyCode);

    if (key == this._keyCode.ESC) {
      if (this.isResponsiveMenu()) {
        let menuButton = this._options.domObj.querySelector("#disclosure-nav-main-nav-toggle");
        this.toggleResponsiveMenu();
        menuButton.focus();
      }
    }
  }

  handleKeyDownSubmenuItem(event) {
    if (event.defaultPrevented) {
      return;
    }

    let key = this.normalizeKey(event.key || event.keyCode);
    let submenuItem = event.target;
    let submenu = submenuItem.parentNode.parentNode;

    if (key == this._keyCode.ESC) {
      if (submenu !== null) {
        let button = this.getButton(submenu);
        if (submenu.classList.contains("disclosure-nav-submenu-open")) {
          this.toggleSubmenu(submenu);
          button.focus();
        }
      }
    }
  }

  handleKeyDownDomObj(event) {
    if (event.defaultPrevented) {
      return;
    }

    let key = this.normalizeKey(event.key || event.keyCode);

    if (key == this._keyCode.ESC) {
      this.closeAllSubmenus();
      this._options.domObj.removeEventListener("keydown", this.handleKeyDownDomObj.bind(this));
    }
  }

  handleMouseoutNavElem(event) {
    if (event.defaultPrevented) {
      return;
    }

    if (
      !this.isResponsiveMenu() &&
      !(this._options.orientation == "vertical" && this._options.collapseVertical == false)
    ) {
      this.closeAllSubmenus();
      this._options.domObj.removeEventListener("keydown", this.handleKeyDownDomObj.bind(this));
    }
  }

  handleMouseoverNavItem(event) {
    if (event.defaultPrevented) {
      return;
    }

    let navItem = event.target;
    let submenu = this.getSubmenu(navItem);

    if (
      !this.isResponsiveMenu() &&
      !(this._options.orientation == "vertical" && this._options.collapseVertical == false)
    ) {
      this.closeSiblingSubmenus(navItem);

      if (submenu != null) {
        this.openSubmenu(submenu);
        this._options.domObj.addEventListener("keydown", this.handleKeyDownDomObj.bind(this), {
          once: true,
        });
      }
    }
  }

  handleWindowResize(event) {
    if (event.defaultPrevented) {
      return;
    }

    this.resize();
  }

  handleClickMenuToggle(event) {
    if (event.defaultPrevented) {
      return;
    }

    this.toggleResponsiveMenu();
  }

  // Utility functions --------------------------------------------------------

  getButton(submenu) {
    let submenuId = submenu.getAttribute("id");
    let button = this._options.domObj.querySelector("button[aria-controls=" + submenuId + "]");

    return button;
  }

  getSubmenu(node) {
    let elemType = node.nodeName;
    let submenu = null;

    if (elemType == "A" || elemType == "SPAN" || elemType == "BUTTON") {
      submenu = node.parentNode.querySelector(".disclosure-nav-submenu");
    } else if (elemType == "LI") {
      submenu = node.querySelector(".disclosure-nav-submenu");
    }

    return submenu;
  }

  submenuIsOpen(submenu) {
    let result = submenu.classList.contains("disclosure-nav-submenu-open") ? true : false;

    return result;
  }

  submenuIsClosed(submenu) {
    let result = submenu.classList.contains("disclosure-nav-submenu-closed") ? true : false;

    return result;
  }

  toggleSubmenu(submenu) {
    if (this.submenuIsOpen(submenu)) {
      this.closeSubmenu(submenu);
    } else if (this.submenuIsClosed(submenu)) {
      this.openSubmenu(submenu);
    }
  }

  openSubmenu(submenu) {
    let button = this.getButton(submenu);

    submenu.classList.add("disclosure-nav-submenu-open");
    submenu.classList.remove("disclosure-nav-submenu-closed");

    button.setAttribute("aria-expanded", "true");
  }

  closeSubmenu(submenu) {
    let button = this.getButton(submenu);

    submenu.classList.add("disclosure-nav-submenu-closed");
    submenu.classList.remove("disclosure-nav-submenu-open");

    button.setAttribute("aria-expanded", "false");
  }

  isTopLevelButton(button) {
    let result = button.classList.contains("disclosure-nav-top-level");

    return result;
  }

  closeAllSubmenus() {
    let allOpenSubmenus = this._navElem.querySelectorAll(".disclosure-nav-submenu-open");

    for (let i = 0; i < allOpenSubmenus.length; i++) {
      this.closeSubmenu(allOpenSubmenus[i]);
    }
  }

  closeSiblingSubmenus(node) {
    let elemType = node.nodeName;
    let isSubmenu = elemType == "UL" && node.classList.contains("disclosure-nav-submenu");
    let submenu = null;
    let siblingSubmenus = [];

    if (isSubmenu) {
      submenu = node;
    } else {
      submenu = this.getSubmenu(node);
    }

    if (elemType == "UL" || elemType == "A" || elemType == "SPAN" || elemType == "BUTTON") {
      siblingSubmenus = node.parentNode.parentNode.querySelectorAll("li > ul");
    } else if (elemType == "LI") {
      siblingSubmenus = node.parentNode.querySelectorAll("li > ul");
    }

    for (let i = 0; i < siblingSubmenus.length; i++) {
      if (siblingSubmenus[i] != submenu) {
        this.closeSubmenu(siblingSubmenus[i]);
      }
    }
  }

  openParentSubmenus(element) {
    // Open all submenus above and including the element.
    let submenu = element.parentNode.parentNode;

    if (submenu.classList.contains("disclosure-nav-submenu-closed")) {
      let button = submenu.parentNode.querySelector('button[aria-expanded="false"]');

      if (button != null) {
        this.openSubmenu(submenu);
        this.openParentSubmenus(submenu);
      }
    }
  }

  expandToActiveMenuitem() {
    let activeClass = "." + this._options.activeClass;
    let activeElement = this._navElem.querySelector(activeClass);

    if (activeElement != null) {
      this.openParentSubmenus(activeElement);
    }
  }

  expandActiveSubmenu() {
    let activeClass = "." + this._options.activeClass;
    let activeElement = this._navElem.querySelector(activeClass);

    if (activeElement != null) {
      let submenu = this.getSubmenu(activeElement);
      if (submenu != null) {
        this.openSubmenu(submenu);
      }
    }
  }

  switchOrientation() {
    let navElem = this._navElem;
    let currentOrientation = this._options.orientation;

    if (currentOrientation === "horizontal") {
      this._options.orientation = "vertical";
      navElem.classList.remove("disclosure-nav-orientation-horizontal");
      navElem.classList.add("disclosure-nav-orientation-vertical");
    } else if (currentOrientation === "vertical") {
      this._options.orientation = "horizontal";
      navElem.classList.remove("disclosure-nav-orientation-vertical");
      navElem.classList.add("disclosure-nav-orientation-horizontal");
    }

    // Toggle orientation switched flag.
    this._orientationSwitched = !this._orientationSwitched;
  }

  addMenuToggle() {
    if (this._options.domObj.getElementById(this._menuToggle.getAttribute("id")) == null) {
      this._navElem.parentNode.insertBefore(this._menuToggle, this._navElem);
      // Add closed class to disclosure nav.
      this._navElem.classList.add("disclosure-nav-responsive");
      this._navElem.classList.add("disclosure-nav-closed");
      this._menuToggle.setAttribute("aria-expanded", "false");
    }
  }

  removeMenuToggle() {
    if (this._options.domObj.getElementById(this._menuToggle.getAttribute("id"))) {
      this._navElem.parentNode.removeChild(this._menuToggle);
      // Remove open/closed classes from menu.
      this._navElem.classList.remove("disclosure-nav-responsive");
      this._navElem.classList.remove("disclosure-nav-open");
      this._navElem.classList.remove("disclosure-nav-closed");
      this._menuToggle.setAttribute("aria-expanded", "false");
    }
  }

  isResponsiveMenu() {
    let isResponsiveMenu = this._navElem.classList.contains("disclosure-nav-responsive");

    return isResponsiveMenu;
  }

  toggleResponsiveMenu() {
    if (this._navElem.classList.contains("disclosure-nav-closed")) {
      this._navElem.classList.remove("disclosure-nav-closed");
      this._navElem.classList.add("disclosure-nav-open");
      this._menuToggle.setAttribute("aria-expanded", "true");
    } else if (this._navElem.classList.contains("disclosure-nav-open")) {
      this._navElem.classList.remove("disclosure-nav-open");
      this._navElem.classList.add("disclosure-nav-closed");
      this._menuToggle.setAttribute("aria-expanded", "false");
      this.closeAllSubmenus();
    }
  }

  resize() {
    let viewportWidth = this._options.windowObj.innerWidth;
    let breakpointMinWidth = this._options.breakpointMinWidth;

    if (viewportWidth <= breakpointMinWidth) {
      this.addMenuToggle();

      if (this._options.orientation === "horizontal") {
        this.switchOrientation();
      }
    } else {
      if (this.isResponsiveMenu()) {
        // Hide menu toggle.
        this.removeMenuToggle();

        // Collapse any submenus that were open with responsive menu.
        this.closeAllSubmenus();

        if (this._orientationSwitched && this._options.orientation === "vertical") {
          this.switchOrientation();
        }
      }
    }
  }

  normalizeKey(key) {
    let normalizedKey = null;

    switch (key) {
      case "Tab":
      case 9:
        normalizedKey = this._keyCode.TAB;
        break;

      case "Enter":
      case 13:
        normalizedKey = this._keyCode.ENTER;
        break;

      case "Escape":
      case "Esc":
      case 27:
        normalizedKey = this._keyCode.ESC;
        break;

      case "Spacebar":
      case " ":
      case 32:
        normalizedKey = this._keyCode.SPACE;
        break;

      case "End":
      case 35:
        normalizedKey = this._keyCode.END;
        break;

      case "Home":
      case 36:
        normalizedKey = this._keyCode.HOME;
        break;

      case "Left":
      case "ArrowLeft":
      case 37:
        normalizedKey = this._keyCode.ARROW_LEFT;
        break;

      case "Up":
      case "ArrowUp":
      case 38:
        normalizedKey = this._keyCode.ARROW_UP;
        break;

      case "Right":
      case "ArrowRight":
      case 39:
        normalizedKey = this._keyCode.ARROW_RIGHT;
        break;

      case "Down":
      case "ArrowDown":
      case 40:
        normalizedKey = this._keyCode.ARROW_DOWN;
        break;
    }

    return normalizedKey;
  }
}
